import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calculator } from "lucide-react";
import { toast } from "sonner";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";

const C = 299792458; // speed of light in m/s

export default function TimeDilationCalculator() {
  const [properTime, setProperTime] = useState("");
  const [velocity, setVelocity] = useState("");
  const [result, setResult] = useState<number | null>(null);
  const [gamma, setGamma] = useState<number | null>(null);

  const calculateDilation = () => {
    const t0 = parseFloat(properTime);
    const v = parseFloat(velocity);

    if (isNaN(t0) || isNaN(v)) {
      toast.error("Please enter valid values");
      return;
    }

    if (v >= C) {
      toast.error("Velocity must be less than speed of light");
      return;
    }

    const beta = v / C;
    const gammaFactor = 1 / Math.sqrt(1 - beta * beta);
    const dilatedTime = gammaFactor * t0;

    setGamma(gammaFactor);
    setResult(dilatedTime);
    toast.success("Calculation complete!");
  };

  const reset = () => {
    setProperTime("");
    setVelocity("");
    setResult(null);
    setGamma(null);
  };

  return (
    <PageLayout calculatorName="Time Dilation Calculator" calculatorPath="/calculator/time-dilation">
      <div className="max-w-2xl mx-auto">
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>Formula: t = γt₀ where γ = 1/√(1-v²/c²)</CardTitle>
            <CardDescription>
              Time slows down at high velocities
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="properTime">Proper Time t₀ (s)</Label>
                <Input
                  id="properTime"
                  type="number"
                  step="any"
                  placeholder="Time in moving frame"
                  value={properTime}
                  onChange={(e) => setProperTime(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="velocity">Velocity v (m/s)</Label>
                <Input
                  id="velocity"
                  type="number"
                  step="any"
                  placeholder="Speed of object"
                  value={velocity}
                  onChange={(e) => setVelocity(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Speed of light c = 299,792,458 m/s
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <Button onClick={calculateDilation} className="flex-1">
                <Calculator className="mr-2 h-4 w-4" />
                Calculate
              </Button>
              <Button onClick={reset} variant="outline">
                Reset
              </Button>
            </div>

            {result !== null && gamma !== null && (
              <>
                <div className="space-y-4">
                  <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                    <CardHeader>
                      <CardTitle className="text-xl">Dilated Time</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold text-primary">
                        {result.toFixed(6)} s
                      </p>
                      <p className="text-sm text-muted-foreground mt-2">
                        Time measured by stationary observer
                      </p>
                    </CardContent>
                  </Card>

                  <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                    <CardHeader>
                      <CardTitle className="text-xl">Lorentz Factor (γ)</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold text-primary">
                        {gamma.toFixed(6)}
                      </p>
                      <p className="text-sm text-muted-foreground mt-2">
                        v/c = {(parseFloat(velocity) / C).toFixed(6)}
                      </p>
                    </CardContent>
                  </Card>
                </div>
                
                <CalculatorSuperCalcCTA config={calculatorCTAs["time-dilation"]} trigger={result} />
              </>
            )}

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-lg">Key Concepts</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm text-muted-foreground">
                <p>• Time passes slower for moving objects</p>
                <p>• Effect becomes significant near speed of light</p>
                <p>• At 0.9c, time runs at ~44% normal rate</p>
                <p>• Proper time is minimum (in object's frame)</p>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
